import { loadClient } from '@cx-modules/odds-grid';
import createI18nInstance from '../../services/i18n/index.js';
import ContextService from '../../services/context/index.js';

// Services
import { Analytics } from '../../services/analytics.js';
import {
  cxModuleLoader,
  fetchCampaignData,
  render
} from '../../services/cx-module-helper';

// Utils
import { isAnalyticsDisabled } from '../../utils/isAnalyticsDisabled/isAnalyticsDisabled.js';
import { html } from '../../utils/lit/html.js';
import { upgradeProperty } from '../../utils/upgradeProperty/upgradeProperty.js';

// Themes
import baseCss from './themes/baseCss';
import gazzettaTheme from './themes/gazzetta';
import jockeyClubTheme from './themes/jockey-club.js';
import jockeyClubCheltenhamTheme from './themes/jockey-club-cheltenham.js';
import jockeyClubWebTheme from './themes/jockey-club-web.js';
import jockeyClubCheltenhamWebTheme from './themes/jockey-club-cheltenham-web.js';
import itTheme from './themes/it';
import usTheme from './themes/us';

import { getFooter } from '../../shared/footer/footer.js';

const WIDGET_VERSION = '1.1.0'; // NOTE: This value should match the last version from the widget's CHANGELOG.md file.
const WIDGET_NAME = 'oc-odds-grid-widget';

const {
  VITE_BOOKIE_CLICKOUT_SERVICE_URL: bookieClickoutServiceUrl,
  VITE_CLICKOUT_SERVICE_URL: clickoutServiceUrl,
  VITE_CX_API_HOST: ocApiHost
} = import.meta.env;

const replaceAffId = (s, id) => {
  return s.replace('{AFF_ID}', id);
};

const wEvent = (name, detail) =>
  new CustomEvent(`${WIDGET_NAME}:${name}`, {
    bubbles: true,
    composed: true,
    detail
  });

const template = document.createElement('template');
template.innerHTML = html`
  <style></style>
  <div id="root"></div>
  <slot name="loading" hidden> Loading... </slot>
  <slot name="error" hidden></slot>
  <slot name="disclaimer" hidden></slot>
`;

class OCOddsGridWidget extends HTMLElement {
  constructor() {
    super();

    if (!this.shadowRoot) {
      this.attachShadow({ mode: 'open' });
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }

    this._version = WIDGET_VERSION;
  }

  get slotId() {
    return this.getAttribute('slot-id');
  }

  set slotId(value) {
    this.setAttribute('slot-id', value);
  }

  get overrideUrl() {
    return this.getAttribute('override-url');
  }

  set overrideUrl(value) {
    this.setAttribute('override-url', value);
  }

  get sport() {
    return this.getAttribute('sport');
  }

  set sport(value) {
    this.setAttribute('sport', value);
  }

  get competition() {
    return this.getAttribute('competition');
  }

  set competition(value) {
    this.setAttribute('competition', value);
  }

  get participant() {
    return this.getAttribute('participant');
  }

  set participant(value) {
    this.setAttribute('participant', value);
  }

  get ocSubeventId() {
    return this.getAttribute('subevent-id');
  }

  set ocSubeventId(value) {
    this.setAttribute('subevent-id', value);
  }

  get theme() {
    return this.getAttribute('theme');
  }

  set theme(value) {
    this.setAttribute('theme', value);
  }

  async connectedCallback() {
    upgradeProperty(this, 'slotId');
    upgradeProperty(this, 'overrideUrl');
    upgradeProperty(this, 'theme');

    this.#bootstrap();
  }

  async #bootstrap() {
    const loadingSlot = this.shadowRoot.querySelector('slot[name="loading"]');
    const errorSlot = this.shadowRoot.querySelector('slot[name="error"]');
    const disclaimerSlot = this.shadowRoot.querySelector(
      'slot[name="disclaimer"]'
    );

    loadingSlot.hidden = false;

    try {
      const target = this.shadowRoot.getElementById('root');

      const [{ Component }, campaignData] = await Promise.all([
        cxModuleLoader(loadClient, this.shadowRoot, {}),
        fetchCampaignData(this.slotId, this.overrideUrl, {
          sport: this.sport,
          competition: this.competition,
          participant: this.participant,
          ocSubeventId: this.ocSubeventId
        })
      ]);

      if (!Component || !target) {
        return;
      }

      ContextService({
        ...campaignData,
        slotId: this.slotId,
        overrideUrl: this.overrideUrl
      });

      const {
        aiContextual,
        affIds,
        apiKey,
        bestOddsStartExpanded,
        bookies,
        campaignName,
        categoryGroupId,
        categoryGroupName,
        clickoutRedirectTime,
        clickoutTemplate,
        enforceMobile,
        firstBetOnMobileStartsExpanded,
        geolocation,
        gridStartsExpanded,
        gtmId,
        marketId,
        marketName,
        minUniqueBookies,
        noDataAvailableFallbackText,
        oddsFormat,
        recommendedMarketId,
        repub,
        showMoreBetsStartsExpanded,
        subeventName,
        trackingRate,
        useDefaultDataLayer,
        widgetStyle
      } = campaignData;

      this.repub = repub;
      this.widgetStyle = this.theme || widgetStyle || 'default';

      let theme;
      let analytics;

      switch (this.widgetStyle) {
        case 'la-gazzetta-delo-sport':
          theme = gazzettaTheme;
          break;
        case 'jockey-club':
          theme = jockeyClubTheme;
          break;
        case 'jockey-club-cheltenham':
          theme = jockeyClubCheltenhamTheme;
          break;
        case 'jockey-club-web':
          theme = jockeyClubWebTheme;
          break;
        case 'jockey-club-cheltenham-web':
          theme = jockeyClubCheltenhamWebTheme;
          break;
        default:
          theme = usTheme;
          break;
      }

      this.shadowRoot.querySelector('style').innerHTML += `${baseCss} ${theme}`;

      if (repub === 'IT') {
        this.shadowRoot.querySelector('style').innerHTML += `${itTheme}`;
      }

      const disableAnalytics = isAnalyticsDisabled(trackingRate);

      if (!disableAnalytics) {
        analytics = new Analytics(
          gtmId,
          useDefaultDataLayer ? 'dataLayer' : undefined
        );
        analytics.integrateAnalytics(); // Create Data Layer

        if ('IntersectionObserver' in window) {
          const intersectionObserver = new IntersectionObserver(entries => {
            const entry = entries[0];

            if (entry?.isIntersecting) {
              analytics.trackEvent({
                action: 'impression',
                category: 'Widget',
                event: 'widget_impression',
                label: 'widget_impression',
                page: window.location.href,
                variation: WIDGET_NAME,
                widgetType: 'odds-grid',
                widgetSlot: this.slotId,
                campaignName,
                ...(recommendedMarketId ? { recommendedMarketId } : {})
              });

              intersectionObserver.disconnect();
            }
          });

          intersectionObserver.observe(this);
        }
      }

      const onOddsButtonClick = (
        action,
        betId,
        bookieCode,
        betName,
        oddsText
      ) => {
        const event = {
          betName,
          bookieCode,
          categoryGroupName,
          clickLocation: action,
          event: 'widget_clickout',
          marketName,
          odds: oddsText,
          subevent: subeventName,
          variation: WIDGET_NAME
        };

        if (!disableAnalytics && analytics) {
          analytics.trackEvent(event);
        }

        const pathParams = new URLSearchParams();

        pathParams.append('affId', affIds[bookieCode]);
        pathParams.append('analytics', disableAnalytics ? 'off' : 'on');
        pathParams.append('bookieCode', bookieCode);
        pathParams.append('redirectInterval', clickoutRedirectTime);
        pathParams.append('template', clickoutTemplate);

        pathParams.append('apiKey', apiKey);
        pathParams.append('betIds', betId);

        if (recommendedMarketId) {
          pathParams.append('recommendedMarketId', recommendedMarketId);
        }

        const redirectUrl = `${clickoutServiceUrl}?${pathParams.toString()}`;

        window.open(redirectUrl, '_blank', 'noopener noreferrer');
      };

      const onOfferClick = (action, bookieCode, clickEvent, clickoutUrl) => {
        clickEvent.preventDefault(); // Stop the redirect so we can use our clickout service.

        if (repub === 'IT') return;

        const pageUrl = window.location.href;

        const bookieLogoContainerDesktop = this.shadowRoot.querySelector(
          '[class^=bookmakersRowWrapper_]'
        );
        const bookieLogoContainerMobile = this.shadowRoot.querySelector(
          '[class^=oddsAreaWrapper_]'
        );

        const bookieLogoContainer = (
          bookieLogoContainerDesktop || bookieLogoContainerMobile
        ).querySelectorAll('[data-bk]');

        const offerIndex = [...bookieLogoContainer].findIndex(
          logoElement => logoElement.dataset.bk === bookieCode
        );

        const event = {
          action,
          bookieCode,
          category: action,
          event: 'offer_clickouts',
          offerIndex,
          offerName: bookies[bookieCode].bookmakerName,
          page: pageUrl,
          repub,
          variation: WIDGET_NAME
        };

        if (!disableAnalytics && analytics) {
          analytics.trackEvent(event);
        }

        const pathParams = new URLSearchParams();

        pathParams.append('affId', affIds[bookieCode]);
        pathParams.append('analytics', disableAnalytics ? 'off' : 'on');
        pathParams.append('bk', bookieCode);
        pathParams.append('redirectInterval', clickoutRedirectTime);
        pathParams.append('template', clickoutTemplate);

        pathParams.append(
          'burl',
          decodeURIComponent(replaceAffId(clickoutUrl, affIds[bookieCode]))
        );
        pathParams.append('page_url', pageUrl);
        pathParams.append('repub', repub);

        const redirectUrl = `${bookieClickoutServiceUrl}?${pathParams.toString()}`;

        window.open(redirectUrl, '_blank', 'noopener noreferrer');
      };

      const portalSelectorFn = () =>
        this.shadowRoot.querySelector('#offer-portal');

      const tooltipSelectorFn = ({ mode, anchor, portal, event }) => {
        const isOurWidget =
          event.target?.tagName?.toLowerCase() === WIDGET_NAME;
        const possibleParentWidget = 'oc-connect-widget';

        // Check if the event target is our widget or a possible parent widget
        if (
          !isOurWidget &&
          event.target?.tagName?.toLowerCase() !== possibleParentWidget
        ) {
          return false;
        }

        const { clientX, clientY } = event;

        const elementAtCursor = this.shadowRoot.elementFromPoint(
          clientX,
          clientY
        );

        if (mode === 'element') {
          return elementAtCursor;
        }

        if (mode === 'contains') {
          if (elementAtCursor) {
            // Check if the element is valid
            const isValid =
              (portal && portal.contains(elementAtCursor)) ||
              anchor.contains(elementAtCursor);

            // If not valid, check if the element is the anchor's parent
            if (!isValid) {
              if (elementAtCursor.contains(anchor)) {
                // Get all child elements to find the closest one to the mouse coordinates
                const childElements = Array.from(elementAtCursor.children);

                // Find the closest element within elementAtCursor
                const closestElement = childElements.reduce(
                  (closest, element) => {
                    const rect = element.getBoundingClientRect();
                    const distance = Math.hypot(
                      rect.left - clientX,
                      rect.top - clientY
                    );

                    // Return the closest element
                    return distance < closest.distance
                      ? { element, distance }
                      : closest;
                  },
                  { element: null, distance: Infinity }
                ).element;

                // Check if the closest element to the cursor is the anchor or the anchor's parent
                const closestIsValid =
                  closestElement === anchor ||
                  closestElement === anchor.parentNode;
                return closestIsValid; // Return validity based on closest element
              }
            }

            return isValid; // Return the original validity check
          }

          return false; // Return false if no elementAtCursor was found
        }
      };

      const i18n = await createI18nInstance(repub);

      const cxProps = {
        apiKey,
        bestOddsStartExpanded,
        bookmakersConfig: bookies,
        dataApiEndpoint: ocApiHost,
        firstBetOnMobileStartsExpanded,
        gridStartsExpanded,
        marketId,
        marketName,
        oddsFormat,
        hideHistory: true,
        showMoreBetsStartsExpanded,
        onOddsButtonClick,
        onOfferClick,
        portalSelectorFn,
        tooltipSelectorFn,
        minUniqueBookies,
        repub,
        categoryGroupId,
        ...(noDataAvailableFallbackText
          ? {
              noDataAvailableFallbackText
            }
          : {}),
        ...(enforceMobile ? { enforceMobile } : {}),
        ...(i18n && { i18n })
      };

      const [countryCode, subdivision] = geolocation.split('-');

      // TODO: more handling for other repubs and geolocations.
      if (repub === 'US') {
        cxProps.countryCode = countryCode === 'us' ? countryCode : 'US';
        cxProps.subdivision = countryCode === 'us' ? subdivision : 'NJ';
      }
      const shouldInjectCxModule = (aiContextual && marketId) || !aiContextual;

      if (this.repub === 'IT' && shouldInjectCxModule) {
        disclaimerSlot.innerHTML = html`${getFooter(this.widgetStyle)}`;
      }

      if (shouldInjectCxModule) {
        render(target, Component, cxProps);
      } else {
        console.log(`Skipping cxm flow - no marketId`);
      }

      this.dispatchEvent(wEvent('load-success', campaignData));
    } catch (error) {
      errorSlot.hidden = false;

      this.dispatchEvent(wEvent('load-error', { error }));
    } finally {
      loadingSlot.hidden = true;

      if (this.repub === 'IT') {
        disclaimerSlot.hidden = false;
      }

      this.dispatchEvent(wEvent('load'));
    }
  }
}

if (window.customElements && !window.customElements.get(WIDGET_NAME)) {
  window.customElements.define(WIDGET_NAME, OCOddsGridWidget);
}
